import * as i0 from '@angular/core';
import { Component, Input, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { coerceNumberProperty } from '@roosevelt/common-ui-lib/core';

/* eslint-disable max-len */
/**
 * Text Mask Component
 *
 * @description this component allows you to hide and show chars of some senstive text
 * @example
 * <!-- rs-doc {link: 'Example of basic text mask'} -->
 * <rs-text-mask color="basic" displayTextLength="4" value="888-888-8888"></rs-text-mask>
 * @example
 * <!-- rs-doc {link: 'Example of primary text mask'} -->
 * <rs-text-mask displayTextLength="4" value="888-888-8888"></rs-text-mask>
 * @example
 * <!-- rs-doc {link: 'Example of secondary text mask'} -->
 * <rs-text-mask color="secondary" displayTextLength="4" value="888-888-8888"></rs-text-mask>
 * @example
 * <!-- rs-doc {link: 'Example of mask with number value'} -->
 * <rs-text-mask color="secondary" displayTextLength="4" [value]="8888888888"></rs-text-mask>
 * @example
 * <!-- rs-doc {link: 'Example of mask with object value'} -->
 * <rs-text-mask color="secondary" displayTextLength="4" [value]="{s: 888-888-8888}"></rs-text-mask>
 * @example
 * <!-- rs-doc {link: 'Example of mask with boolean'} -->
 * <rs-text-mask color="secondary" displayTextLength="3" [value]="true"></rs-text-mask>
 * @example
 * <!-- rs-doc {link: 'Example of mask evaluating to false'} -->
 * <rs-text-mask color="secondary" displayTextLength="4" [value]="false"></rs-text-mask>
 */
class TextMaskComponent {
    /** how many chars should show */
    get displayTextLength() {
        return this._displayTextLength;
    }
    set displayTextLength(value) {
        this._displayTextLength = coerceNumberProperty(value);
    }
    /** allows the host component to change colors, based on a theme */
    get color() {
        return this._color;
    }
    set color(value) {
        this._getHostElement().classList.add(value);
        if (this._color !== value) {
            this._getHostElement().classList.remove(this._color);
        }
        this._color = value;
    }
    /** the full display text that should be shown */
    get value() {
        return this._value;
    }
    set value(val) {
        if (val && typeof val !== 'object') {
            val = val + '';
            this.setDisplayText(val + '');
        }
        else {
            this.displayText = '-';
            this.triggerBtnText = '';
        }
        this._value = val;
    }
    constructor(elementRef) {
        this.elementRef = elementRef;
        /** what should the show link say */
        this.showText = 'View';
        /** what should the hide link say */
        this.hideText = 'Hide';
        /** what char should replace the hidden chars */
        this.fillText = '*';
        /** should this display show the first chars or last chars */
        this.displayDirection = 'end';
        /** time until the display flips back to hidden */
        this.displayTimeOut = 30000;
        this.displayText = '';
        this.triggerBtnText = '';
        this._displayTextLength = 0;
        this._value = '';
        this._color = 'primary';
        this.timeOutReference = undefined;
    }
    ngOnInit() {
        this.color = this.color;
    }
    setDisplayText(val) {
        if (+this.displayTextLength > val.length) {
            console.warn('TEXT_MASK_WARNING', 'input displayTextLength is greater than the value passed');
        }
        this.displayText = (+this.displayTextLength >= val.length) ? val : this.getTextToDisplay(val);
    }
    getTextToDisplay(val) {
        const dLen = this.displayTextLength;
        const starStrng = this.fillText.repeat(val.length - dLen);
        this.triggerBtnText = this.showText;
        return this.displayDirection === 'end' ? starStrng + val.substr(val.length - dLen, dLen) : val.substr(0, dLen) + starStrng;
    }
    triggerShowing(action) {
        if (this.timeOutReference) {
            clearTimeout(this.timeOutReference);
        }
        if (action === this.showText) {
            this.displayText = this.value;
            this.triggerBtnText = this.hideText;
            this.timeOutReference = setTimeout(() => {
                this.displayText = this.getTextToDisplay(this.value);
                clearTimeout(this.timeOutReference);
            }, +this.displayTimeOut);
        }
        else {
            this.displayText = this.getTextToDisplay(this.value);
        }
    }
    _getHostElement() {
        return this.elementRef.nativeElement;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TextMaskComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: TextMaskComponent, isStandalone: true, selector: "rs-text-mask", inputs: { showText: "showText", hideText: "hideText", fillText: "fillText", displayDirection: "displayDirection", displayTimeOut: "displayTimeOut", displayTextLength: "displayTextLength", color: "color", value: "value" }, ngImport: i0, template: "<span class=\"rs-text-mask\">\n  {{displayText}}\n  @if (triggerBtnText) {\n    <a (click)=\"triggerShowing(triggerBtnText)\">({{triggerBtnText}})</a>\n  }\n</span>\n", styles: [".rs-text-mask a{text-decoration:none;cursor:pointer}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TextMaskComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rs-text-mask', standalone: true, template: "<span class=\"rs-text-mask\">\n  {{displayText}}\n  @if (triggerBtnText) {\n    <a (click)=\"triggerShowing(triggerBtnText)\">({{triggerBtnText}})</a>\n  }\n</span>\n", styles: [".rs-text-mask a{text-decoration:none;cursor:pointer}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { showText: [{
                type: Input
            }], hideText: [{
                type: Input
            }], fillText: [{
                type: Input
            }], displayDirection: [{
                type: Input
            }], displayTimeOut: [{
                type: Input
            }], displayTextLength: [{
                type: Input
            }], color: [{
                type: Input
            }], value: [{
                type: Input
            }] } });

class TextMaskModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TextMaskModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: TextMaskModule, imports: [CommonModule,
            TextMaskComponent], exports: [TextMaskComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TextMaskModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: TextMaskModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        TextMaskComponent
                    ],
                    exports: [
                        TextMaskComponent
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { TextMaskComponent, TextMaskModule };
